<script setup lang="ts">

import {faLeftFromBracket} from "@fortawesome/pro-regular-svg-icons";
import Loader from "@/shared/components/ui/loader/Loader.vue";
import {Button} from "@/shared/components/ui/button";
import FormattedAddress from "@/shared/components/address/FormattedAddress.vue";
import {useCheckoutStore} from "@/checkout/stores/checkout.store.ts";
import {computed} from "vue";
import LeafletMap from "@/shared/components/ui/leaflet-map/LeafletMap.vue";
import {useI18n} from "vue-i18n";
import {format as formatDate} from "date-fns-tz";
import {nl} from "date-fns/locale";
import {parseISO} from "date-fns";
const checkout = useCheckoutStore();
const {t} = useI18n()
const isNonBlank = (str: string): boolean => (str?.trim() || "") !== '';
const hasReference = computed(() => isNonBlank(checkout.customerReference));
const hasRemark = computed(() => isNonBlank(checkout.remark));
const hasReferenceOrRemark = computed(() => hasReference.value || hasRemark.value);

const completedReturn = () => {
  window.location.href = checkout.config.completedReturn.url
}

</script>

<template>
  <form class="px-4 pb-36 pt-16 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">
    <div class="mx-auto max-w-lg lg:max-w-none">
      <Loader v-if="checkout.loading"/>
      <template v-else>
        <section aria-labelledby="contact-info-heading">
          <div class="mb-8">
            <h1 class="text-base font-medium text-primary">{{ t('shared.checkout.thanks') }}</h1>
            <p class="mt-2 text-4xl font-bold tracking-tight">{{ t('shared.checkout.order_confirmed') }}</p>
            <p class="mt-2 text-base text-slate-500">{{ t('shared.checkout.process_order_soon') }}</p>
          </div>

          <h2 id="contact-info-heading" class="text-lg font-medium text-gray-900 mb-6">{{ t('shared.checkout.shipping') }}</h2>
          <dl class="grid grid-cols-2 gap-x-6 text-sm">
            <div>
              <dt class="font-medium text-gray-900">{{ t('shared.checkout.delivery_method') }}</dt>
              <dd class="mt-2 text-gray-700">
                <template v-if="checkout.deliveryMethod.title !== ''">
                  {{ checkout.deliveryMethod.title }}
                </template>
                <template v-else>
                  {{((checkout.deliveryMethod.kind === 'PICKUP') ? t('delivery.pickup'):t('delivery.delivery'))}}
                </template>

                <template v-if="checkout.preferredDeliveryDate">
                  <br>
                  {{ t('shared.common.on_date') }} {{ formatDate(parseISO(checkout.preferredDeliveryDate.toString()), 'dd/MM/yyyy', {locale: nl}) }}
                </template>
                <template v-if="checkout.deliveryMethod.kind != 'PICKUP'">
                  (<span v-html="checkout.cost?.deliveryCost.totalIncl?.formatted"/>)
                </template>
                <br>
                <br>
                <FormattedAddress
                    :address="checkout.deliveryAddress"
                    :show-default="false"
                />
              </dd>
            </div>
            <div  v-if="checkout.deliveryAddress.latLng">
              <dt class="font-medium text-gray-900">{{ t('shared.common.location') }}</dt>
              <dd class="mt-2 text-gray-700">
                <LeafletMap style="height: 13rem;width:20rem"
                            class="rounded"
                            :show-controls="false"
                            :points-of-interest="[checkout.deliveryAddress]"
                            :active-point-of-interest="checkout.deliveryAddress"
                />

              </dd>
            </div>
          </dl>
        </section>

        <section aria-labelledby="payment-heading" class="mt-10">
          <h2 id="payment-heading" class="text-lg font-medium text-gray-900 mb-6">{{ t('shared.checkout.payment')}}</h2>
          <dl class="grid grid-cols-2 gap-x-6 text-sm">
            <div>
              <dt class="font-medium text-gray-900">{{ t('shared.common.payment_via') }}</dt>
              <dd class="mt-2 text-gray-700">{{ checkout.paymentMethod?.label }}</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-900">{{ t('shared.common.billing_address') }}</dt>
              <dd class="mt-2 text-gray-700">
                <FormattedAddress
                    :address="checkout.billingAddress"
                    :show-default="false"
                />
              </dd>
            </div>
          </dl>

        </section>

        <section aria-labelledby="shipping-heading" class="mt-10" v-if="hasReferenceOrRemark">
          <h2 id="shipping-heading" class="text-lg font-medium text-gray-900 mb-6">
            {{ t('shared.checkout.extra_info') }}
          </h2>

          <dl v-if="hasReference || hasRemark" class="grid grid-cols-1 gap-x-6 text-sm">
            <div class="mb-default" v-if="hasReference">
              <dt class="font-medium text-gray-900">{{ t('shared.common.own_reference')}}</dt>
              <dd class="mt-2 text-gray-700">{{ checkout.customerReference }}</dd>
            </div>
            <div>
              <dt class="font-medium text-gray-900" v-if="hasRemark">{{ t('shared.common.remarks')}}</dt>
              <dd class="mt-2 text-gray-700 whitespace-pre-line" v-html="checkout.remark"/>
            </div>
          </dl>

        </section>

        <div class="mt-12 flex justify-end items-center">
<!--          <p class="text-sm">Hulp nodig? <a class="text-primary underline hover:cursor-pointer">Neem contact op met ons</a></p>-->
          <Button @click="completedReturn" :icon="faLeftFromBracket" variant="default" class="p-6 w-full">{{ checkout.config.completedReturn.label }}</Button>
        </div>
      </template>
    </div>
  </form>
</template>

<style scoped>

</style>